import React, { useState, useEffect, useRef, forwardRef, useImperativeHandle } from 'react';
import { collection, query, where, getDocs, getDoc, doc } from "firebase/firestore";
import { Helmet } from 'react-helmet-async';
import mapboxgl from 'mapbox-gl';
import 'mapbox-gl/dist/mapbox-gl.css';

import * as utils from './Utils.js';
import { db } from '../firebase.js';
import { shuttleStops, placesOfInterest, categoryColors, categoryLabels } from '../data/transportationLocations.js';
import '../styles/Transportation.scss';

mapboxgl.accessToken = process.env.REACT_APP_MAPBOX_TOKEN;

// ---------------------------------------------------------------------------
// TransportationMap — renders a single mapbox-gl map with all markers
// Exposes flyTo(coords, zoom) and highlightShuttle(shuttleName|null) via ref
// ---------------------------------------------------------------------------
const TransportationMap = forwardRef(({ center, zoom, onShuttleMarkerClick, onPOIMarkerClick }, ref) => {
  const containerRef = useRef(null);
  const mapRef = useRef(null);
  const stopEls = useRef({});
  const poiEls = useRef({});
  const allMarkers = useRef([]);
  const onShuttleMarkerClickRef = useRef(onShuttleMarkerClick);
  const onPOIMarkerClickRef = useRef(onPOIMarkerClick);
  onShuttleMarkerClickRef.current = onShuttleMarkerClick;
  onPOIMarkerClickRef.current = onPOIMarkerClick;

  useImperativeHandle(ref, () => ({
    flyTo(coordinates, flyZoom) {
      if (mapRef.current) {
        mapRef.current.flyTo({ center: coordinates, zoom: flyZoom ?? 15, duration: 1000 });
      }
    },
    reset(initialCenter, initialZoom) {
      const map = mapRef.current;
      if (!map) return;
      allMarkers.current.forEach((m) => m.getPopup()?.remove());
      Object.values(stopEls.current).forEach((el) => el.classList.remove('marker-highlighted'));
      Object.values(poiEls.current).forEach((el) => el.classList.remove('marker-highlighted'));
      map.flyTo({ center: initialCenter, zoom: initialZoom, duration: 800 });
    },
    closeAllPopups() {
      allMarkers.current.forEach((m) => m.getPopup()?.remove());
    },
    highlightShuttle(shuttleName) {
      const map = mapRef.current;
      if (!map) return;

      allMarkers.current.forEach((m) => m.getPopup()?.remove());

      Object.entries(stopEls.current).forEach(([id, el]) => {
        const stop = shuttleStops.find((s) => s.id === id);
        if (!shuttleName || stop.shuttle !== shuttleName) {
          el.classList.remove('marker-highlighted');
        } else {
          el.classList.add('marker-highlighted');
        }
      });

      if (shuttleName) {
        const stops = shuttleStops.filter((s) => s.shuttle === shuttleName);
        if (stops.length === 1) {
          map.flyTo({ center: stops[0].coordinates, zoom: 15, duration: 800 });
        } else {
          const bounds = stops.reduce(
            (b, s) => b.extend(s.coordinates),
            new mapboxgl.LngLatBounds(stops[0].coordinates, stops[0].coordinates)
          );
          map.fitBounds(bounds, { padding: 60, duration: 800 });
        }
      }
    },
    highlightPOI(category) {
      const map = mapRef.current;
      if (!map) return;

      allMarkers.current.forEach((m) => m.getPopup()?.remove());

      Object.entries(poiEls.current).forEach(([id, el]) => {
        const poi = placesOfInterest.find((p) => p.id === id);
        if (!category || poi.category !== category) {
          el.classList.remove('marker-highlighted');
        } else {
          el.classList.add('marker-highlighted');
        }
      });

      if (category) {
        const poi = placesOfInterest.find((p) => p.category === category);
        if (poi) map.flyTo({ center: poi.coordinates, zoom: 15, duration: 800 });
      }
    },
  }));

  useEffect(() => {
    if (mapRef.current || !containerRef.current) return;

    const map = new mapboxgl.Map({
      container: containerRef.current,
      style: 'mapbox://styles/chandlercrane/cmoa0h3bf004d01s8fctb842u',
      center: center,
      zoom: zoom,
    });

    map.addControl(new mapboxgl.NavigationControl(), 'top-right');

    map.on('load', () => {
      // Shuttle stops
      shuttleStops.forEach((stop) => {
        const el = createMarkerElement(categoryColors.shuttleStop);
        stopEls.current[stop.id] = el;
        el.addEventListener('click', () => onShuttleMarkerClickRef.current?.(stop.shuttle));
        const popup = new mapboxgl.Popup({ offset: 25, className: 'transportation-popup' }).setHTML(
          popupHTML(stop, true)
        );
        allMarkers.current.push(new mapboxgl.Marker(el).setLngLat(stop.coordinates).setPopup(popup).addTo(map));
      });

      // Places of interest
      placesOfInterest.forEach((poi) => {
        const color = categoryColors[poi.category] || categoryColors.other;
        const el = createMarkerElement(color);
        poiEls.current[poi.id] = el;
        el.addEventListener('click', () => onPOIMarkerClickRef.current?.(poi.category));
        const popup = new mapboxgl.Popup({ offset: 25, className: 'transportation-popup' }).setHTML(
          popupHTML(poi, false)
        );
        allMarkers.current.push(new mapboxgl.Marker(el).setLngLat(poi.coordinates).setPopup(popup).addTo(map));
      });
    });

    mapRef.current = map;

    return () => {
      map.remove();
      mapRef.current = null;
      stopEls.current = {};
      poiEls.current = {};
      allMarkers.current = [];
    };
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  return <div ref={containerRef} className="transportation-map-canvas" />;
});

function createMarkerElement(color) {
  const el = document.createElement('div');
  el.className = 'transportation-marker';
  el.style.backgroundColor = color;
  return el;
}

function popupHTML(location, isStop) {
  let html = `<p class="popup-name">${location.name}</p>`;
  html += `<p class="popup-address">${location.address}</p>`;
  if (isStop) {
    html += `<p class="popup-shuttle">${location.shuttle}</p>`;
    if (location.pickupTime) {
      html += `<p class="popup-time">Pickup: ${location.pickupTime}</p>`;
    }
  }
  return html;
}

// ---------------------------------------------------------------------------
// Main Transportation page
// ---------------------------------------------------------------------------
// Lookup a POI by category to get its coordinates
function poiCoordinatesForCategory(category) {
  const poi = placesOfInterest.find((p) => p.category === category);
  return poi ? poi.coordinates : null;
}

const Transportation = () => {
  const [lastName, setLastName] = useState("");
  const [result, setResult] = useState(null);
  const [matchingReservations, setMatchingReservations] = useState([]);
  const [matchingGuests, setMatchingGuests] = useState([]);
  const [errorMessage, setErrorMessage] = useState("");
  const [loading, setLoading] = useState(false);
  const newportMapRef = useRef(null);
  const [selectedShuttle, setSelectedShuttle] = useState(null);
  const [selectedPOI, setSelectedPOI] = useState(null);

  const handleReset = () => {
    setSelectedShuttle(null);
    setSelectedPOI(null);
    newportMapRef.current?.reset([-71.3274, 41.4901], 11);
  };

  const handleShuttleClick = (shuttleName) => {
    const next = selectedShuttle === shuttleName ? null : shuttleName;
    setSelectedShuttle(next);
    setSelectedPOI(null);
    newportMapRef.current?.highlightShuttle(next);
    newportMapRef.current?.highlightPOI(null);
  };

  const handleLegendClick = (key) => {
    const next = selectedPOI === key ? null : key;
    setSelectedPOI(next);
    setSelectedShuttle(null);
    newportMapRef.current?.highlightPOI(next);
    newportMapRef.current?.highlightShuttle(null);
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    setErrorMessage("");
    setResult(null);
    setMatchingReservations([]);
    setMatchingGuests([]);

    if (!lastName.trim()) {
      setErrorMessage("Please enter your last name.");
      return;
    }

    setLoading(true);

    try {
      const cleanedName = lastName.toLowerCase().replace(/[^a-z]/g, "");

      // Run both queries in parallel — reservations by lookup_names and all guests by last name
      const resQuery = query(collection(db, "reservations"), where("lookup_names", "array-contains", cleanedName));
      const [resSnapshot, guestSnapshot] = await Promise.all([
        getDocs(resQuery),
        getDocs(collection(db, "guests")),
      ]);

      const reservationsFromLookup = resSnapshot.docs.map((d) => ({ id: d.id, ...d.data() }));

      // Filter guests whose last name matches and have RSVP'd yes to at least one event
      const matchedGuests = guestSnapshot.docs
        .map((d) => ({ id: d.id, ...d.data() }))
        .filter((g) => {
          const parts = (g.name || "").trim().toLowerCase().split(/\s+/);
          const gLastName = parts[parts.length - 1]?.replace(/[^a-z]/g, "") || "";
          if (gLastName !== cleanedName) return false;
          return (g.events || []).some((e) => e.rsvp === "yes");
        });

      // Find reservation IDs from guest matches not already covered by lookup_names
      const existingResIds = new Set(reservationsFromLookup.map((r) => r.id));
      const additionalResIds = [...new Set(
        matchedGuests.map((g) => g.reservation_id).filter((id) => id && !existingResIds.has(id))
      )];

      let additionalReservations = [];
      if (additionalResIds.length > 0) {
        const additionalDocs = await Promise.all(
          additionalResIds.map((id) => getDoc(doc(db, "reservations", id)))
        );
        additionalReservations = additionalDocs.filter((d) => d.exists()).map((d) => ({ id: d.id, ...d.data() }));
      }

      const allReservations = [...reservationsFromLookup, ...additionalReservations];

      if (allReservations.length === 0) {
        setErrorMessage("No reservation found with that last name. Please double-check the spelling.");
        setLoading(false);
        return;
      }

      if (allReservations.length === 1) {
        resolveShuttleResult(allReservations[0]);
      } else {
        setMatchingReservations(allReservations);
      }
    } catch (err) {
      console.error(err);
      setErrorMessage("An error occurred while searching. Please try again.");
    } finally {
      setLoading(false);
    }
  };

  const resolveShuttleResult = async (reservation) => {
    const guestIds = reservation.guests || [];
    if (!guestIds.length) {
      setResult({ walking: true, name: reservation.name, guestNames: [] });
      return;
    }

    const guestDocs = await Promise.all(
      guestIds.map((id) => getDoc(doc(db, "guests", id.toString())))
    );
    const guests = guestDocs
      .filter((d) => d.exists())
      .map((d) => ({ id: d.id, ...d.data() }))
      .filter((g) => (g.events || []).some((e) => e.rsvp === "yes"));

    if (!guests.length) {
      setErrorMessage("No reservation found with that last name. Please double-check the spelling.");
      return;
    }

    const distinctShuttles = [...new Set(guests.map((g) => g.shuttle || null))];
    const guestNames = guests.map((g) => g.name).filter(Boolean);

    if (distinctShuttles.length === 1) {
      const shuttle = distinctShuttles[0];
      if (shuttle === "any") {
        setResult({ any: true, name: reservation.name, guestNames });
      } else if (shuttle) {
        setResult({ shuttle, name: reservation.name, guestNames });
        setSelectedShuttle(shuttle);
        setSelectedPOI(null);
        newportMapRef.current?.highlightShuttle(shuttle);
        newportMapRef.current?.highlightPOI(null);
      } else {
        setResult({ walking: true, name: reservation.name, guestNames });
      }
    } else {
      // Guests have different shuttles — ask which one they are
      setMatchingGuests(guests.map((g) => ({ ...g, resName: reservation.name })));
    }
  };

  const handleSelectReservation = (res) => {
    setMatchingReservations([]);
    resolveShuttleResult(res);
  };

  const handleSelectGuest = (guest) => {
    setMatchingGuests([]);
    const guestNames = [guest.name].filter(Boolean);
    if (guest.shuttle === "any") {
      setResult({ any: true, name: guest.resName, guestNames });
    } else if (guest.shuttle) {
      setResult({ shuttle: guest.shuttle, name: guest.resName, guestNames });
      setSelectedShuttle(guest.shuttle);
      setSelectedPOI(null);
      newportMapRef.current?.highlightShuttle(guest.shuttle);
      newportMapRef.current?.highlightPOI(null);
    } else {
      setResult({ walking: true, name: guest.resName, guestNames });
    }
  };

  const handleBack = () => {
    setMatchingReservations([]);
    setMatchingGuests([]);
    setResult(null);
    setErrorMessage("");
    setLastName("");
    setSelectedShuttle(null);
    setSelectedPOI(null);
    newportMapRef.current?.highlightShuttle(null);
    newportMapRef.current?.highlightPOI(null);
  };

  const showForm = !result && matchingReservations.length === 0 && matchingGuests.length === 0;

  const legendCategories = [
    { key: "shuttleStop", color: categoryColors.shuttleStop, label: categoryLabels.shuttleStop },
    ...Object.keys(categoryColors)
      .filter((k) => k !== "shuttleStop" && placesOfInterest.some((p) => p.category === k))
      .map((k) => ({ key: k, color: categoryColors[k], label: categoryLabels[k] })),
  ];

  return (
    <div className="Transportation">
      <Helmet>
        <title>Transportation | Chandler &amp; Anna</title>
        <meta name="description" content="Shuttle information and maps for the Chandler &amp; Anna wedding ceremony on Saturday, April 25th." />
        <meta property="og:title" content="Transportation | Chandler &amp; Anna" />
        <meta property="og:description" content="Shuttle information and maps for the wedding ceremony on Saturday, April 25th." />
        <meta property="og:url" content="https://chandlerandanna.com/transportation" />
        <meta name="robots" content="noindex,nofollow" />
      </Helmet>

      <div className="centered-box wedding-page-block">
        <div className="wedding-centerpiece">
          <p className="wedding-page-title">Transportation</p>

          <div className="transportation-intro">
            <p className="wedding-page-info">
              This page shows the shuttle map for getting to the wedding ceremony on Saturday, April 25th.
            </p>
            <p className="wedding-page-info">
              Enter your last name below to see which shuttle you're assigned to. Please use the shuttle you're assigned to so we can ensure everyone gets to the venue on time.
            </p>
          </div>

          {/* Search */}
          <div className="transportation-search-container">
            {showForm && (
              <div className="wedding-form-container">
                <form className="wedding-form" onSubmit={handleSearch}>
                  {errorMessage && (
                    <div className="alert alert-primary-wedding" role="alert">
                      {errorMessage}
                    </div>
                  )}
                  <div className="mb-3">
                    <label htmlFor="inputLastName" className="form-label">Last Name</label>
                    <input
                      type="text"
                      className="form-control"
                      id="inputLastName"
                      placeholder="Enter your last name"
                      value={lastName}
                      onChange={(e) => setLastName(e.target.value)}
                    />
                  </div>
                  <button className="btn btn-navyblue" type="submit" disabled={loading}>
                    {loading ? "Searching..." : "Find my shuttle"}
                  </button>
                </form>
              </div>
            )}

            {/* Multiple guests with different shuttles */}
            {matchingGuests.length > 0 && (
              <div className="multiple-reservation-container">
                <div className="alert alert-primary-wedding" role="alert">
                  Multiple guests found on this reservation. Please select your name.
                </div>
                <button className="btn btn-outline-navyblue mb-3" onClick={handleBack}>
                  Back to Search
                </button>
                <div>
                  {matchingGuests.map((guest) => (
                    <div key={guest.id} className="mb-3 p-3 border rounded d-flex align-items-center justify-content-between">
                      <p style={{ fontWeight: 700, marginBottom: 0 }}>{guest.name}</p>
                      <button className="btn btn-navyblue" onClick={() => handleSelectGuest(guest)}>
                        Select
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Multiple matches */}
            {matchingReservations.length > 1 && (
              <div className="multiple-reservation-container">
                <div className="alert alert-primary-wedding" role="alert">
                  Multiple reservations found. Please choose yours.
                </div>
                <button className="btn btn-outline-navyblue mb-3" onClick={handleBack}>
                  Back to Search
                </button>
                <div>
                  {matchingReservations.map((res) => (
                    <div key={res.id} className="mb-3 p-3 border rounded d-flex align-items-center justify-content-between">
                      <p style={{ fontWeight: 700, marginBottom: 0 }}>{res.name}</p>
                      <button className="btn btn-navyblue" onClick={() => handleSelectReservation(res)}>
                        Select
                      </button>
                    </div>
                  ))}
                </div>
              </div>
            )}

            {/* Any shuttle result */}
            {result && result.any && (
              <div className="transportation-result">
                <p className="transportation-result-title">{result.name}</p>
                {result.guestNames?.length > 0 && (
                  <p className="transportation-result-guests">{result.guestNames.join(", ")}</p>
                )}
                <p className="transportation-result-detail" style={{ marginTop: '4px' }}>
                  You're welcome to board any available shuttle to the ceremony.
                </p>
                <button className="btn btn-outline-navyblue mt-2" onClick={handleBack} style={{ fontSize: '12px' }}>
                  Search again
                </button>
              </div>
            )}

            {/* Shuttle result */}
            {result && !result.walking && !result.any && (
              <div className="transportation-result">
                <p className="transportation-result-title">{result.name}</p>
                {result.guestNames?.length > 0 && (
                  <p className="transportation-result-guests">{result.guestNames.join(", ")}</p>
                )}
                {(() => {
                  const stops = shuttleStops.filter((s) => s.shuttle === result.shuttle);
                  return stops.length > 0 ? (
                    <div className="transportation-result-stop">
                      {stops.map((stop) => (
                        <div key={stop.id}>
                          <p className="transportation-result-detail">
                            <strong>Pick-Up Location:</strong> {stop.name}
                          </p>
                          {stop.pickupTime && (
                            <p className="transportation-result-detail">
                              <strong>Pick-Up Time:</strong> {stop.pickupTime}
                            </p>
                          )}
                        </div>
                      ))}
                    </div>
                  ) : null;
                })()}
                <button className="btn btn-outline-navyblue mt-2" onClick={handleBack} style={{ fontSize: '12px' }}>
                  Search again
                </button>
              </div>
            )}

            {/* Walking result */}
            {result && result.walking && (
              <div className="transportation-result">
                <p className="transportation-result-title">{result.name}</p>
                {result.guestNames?.length > 0 && (
                  <p className="transportation-result-guests">{result.guestNames.join(", ")}</p>
                )}
                <p className="transportation-result-detail" style={{ marginTop: '4px' }}>
                  You're within walking distance — no shuttle needed!
                </p>
                <button className="btn btn-outline-navyblue mt-2" onClick={handleBack} style={{ fontSize: '12px' }}>
                  Search again
                </button>
              </div>
            )}
          </div>

          {/* Maps */}
          <div className="transportation-maps-section">
            <p className="transportation-maps-title">Shuttle Map</p>

            {/* Shuttle legend dot + list */}
            {(() => {
              const shuttleLegend = legendCategories.find((c) => c.key === 'shuttleStop');
              return shuttleLegend && (
                <div className="legend-item shuttle-legend-header">
                  <div className="legend-dot" style={{ backgroundColor: shuttleLegend.color }} />
                  <span>{shuttleLegend.label}</span>
                </div>
              );
            })()}
            <div className="transportation-shuttle-list">
              {[...new Set(shuttleStops.map((s) => s.shuttle))].map((shuttleName) => {
                const stops = shuttleStops.filter((s) => s.shuttle === shuttleName);
                return (
                  <div
                    key={shuttleName}
                    className={`transportation-shuttle-item${selectedShuttle === shuttleName ? ' shuttle-selected' : ''}`}
                    onClick={() => handleShuttleClick(shuttleName)}
                  >
                    <span className="transportation-shuttle-name">{shuttleName}:</span>
                    {stops.map((stop, i) => (
                      <span key={stop.id} className="transportation-stop-entry">
                        {stop.name} <span className="transportation-stop-time">({stop.pickupTime})</span>{i < stops.length - 1 ? ',' : ''}
                      </span>
                    ))}
                  </div>
                );
              })}
            </div>

            {/* POI legend */}
            <div className="transportation-legend">
              {legendCategories.filter((c) => c.key !== 'shuttleStop').map(({ key, color, label }) => {
                const clickable = !!poiCoordinatesForCategory(key);
                const poi = placesOfInterest.find((p) => p.category === key);
                return (
                  <div
                    key={key}
                    className={`legend-item${clickable ? ' legend-item-clickable' : ''}${selectedPOI === key ? ' legend-item-selected' : ''}`}
                    onClick={clickable ? () => handleLegendClick(key) : undefined}
                  >
                    <div className="legend-dot" style={{ backgroundColor: color }} />
                    <span><span className="transportation-shuttle-name">{label}:</span> {poi ? poi.name : label}</span>
                  </div>
                );
              })}
            </div>

            <div className="transportation-map-reset-row">
              <button className="btn btn-outline-navyblue btn-sm" onClick={handleReset}>Reset Map</button>
            </div>

            <div className="transportation-maps-row">
              <div className="transportation-map-wrap">
                <TransportationMap
                  ref={newportMapRef}
                  center={[-71.3274, 41.4901]}
                  zoom={11}
                  onShuttleMarkerClick={handleShuttleClick}
                  onPOIMarkerClick={handleLegendClick}
                />
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="centered-box">
        {utils.displayCheckerboard(20, 42, ['seersucker-blue', 'ivory'])}
      </div>
    </div>
  );
};

export default Transportation;
